/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment/moment';

import { DisplayType } from 'common/types';
import { GRID_NAME } from 'common/types/datagrid';
import DateBeforeAfter from 'common/components/filters/advanced-filter-components/DateBeforeAfter';
import DataGridAdvancedFiltersMenuSection from 'common/components/data-grid/DataGridAdvancedFiltersMenuSection';
import RadioGroup from 'common/components/form/RadioGroup';
import { useAdvancedFilters } from 'common/context/DataGridAdvancedFilterContext';
import {
    CLIENT_STATUS,
    ClientAPI,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import {
    useClientsQuery,
    useCountriesQuery,
    useQuoteStatusesQuery,
} from 'common/stores/redux/MetaStore/MetaStoreApi';
import { selectAdvancedFilters } from 'common/stores/redux/DataGrid/DataGrid.selectors';
import DataGridAdvancedFilterFormSelect from 'common/components/data-grid/DataGridAdvancedFilterFormSelect';

export const ALLOWED_STATUSES = ['CREATED', 'APPROVED', 'CLOSED', 'CANCELLED'];
export const ALLOWED_CLIENT_STATUSES = [
    CLIENT_STATUS.APPROVED,
    CLIENT_STATUS.ENABLED,
];

const dataTestPrefix = 'admin-quotes-equipment-only-advanced-filter';

export const AdminQuotesAdvancedFilterDataGrid = () => {
    const {
        advancedFilters,
        setAdvancedFilters,
        handleFormSelectChangeSingle,
        handleFormSelectChangeMultiple,
        handleRadioButtonGroupChange,
        handleDateChange,
    } = useAdvancedFilters();

    const clientOptions = useClientOptions();
    const countryOptions = useCountryOptions();
    const statusOptions = useStatusOptions();
    const plannedOptions = usePlannedOptions();

    const initialStoreFilters = useSelector(state =>
        selectAdvancedFilters(state, GRID_NAME.ADMIN_QUOTE_EQUIPMENT_ONLY)
    );

    useEffect(() => {
        setAdvancedFilters(initialStoreFilters);
    }, [initialStoreFilters, setAdvancedFilters]);

    return (
        <>
            <DataGridAdvancedFiltersMenuSection
                id='exclude-client'
                title='Exclude Client'
                advancedFilterKeys={['excludeClient']}
                clearSectionFilters={() =>
                    handleFormSelectChangeMultiple([], 'excludeClient')
                }
            >
                <DataGridAdvancedFilterFormSelect
                    label='SELECT CLIENT'
                    controlId={`${dataTestPrefix}-exclude-client-select`}
                    dataTestId={`${dataTestPrefix}-exclude-client-select`}
                    options={clientOptions}
                    value={advancedFilters.excludeClient?.map(
                        client => client.form
                    )}
                    onChange={value =>
                        handleFormSelectChangeMultiple(
                            value,
                            'excludeClient',
                            'Exclude'
                        )
                    }
                    searchable
                    multiple
                    clearable
                />
            </DataGridAdvancedFiltersMenuSection>
            <DataGridAdvancedFiltersMenuSection
                id='status'
                title='Status'
                advancedFilterKeys={['status']}
                clearSectionFilters={() =>
                    handleFormSelectChangeMultiple([], 'status')
                }
            >
                <DataGridAdvancedFilterFormSelect
                    label='SELECT STATUS'
                    controlId={`${dataTestPrefix}-status-select`}
                    dataTestId={`${dataTestPrefix}-status-select`}
                    options={statusOptions}
                    value={advancedFilters.status?.map(line => line.form)}
                    onChange={value =>
                        handleFormSelectChangeMultiple(
                            value,
                            'status',
                            'Status is'
                        )
                    }
                    multiple
                    clearable
                />
            </DataGridAdvancedFiltersMenuSection>
            <DataGridAdvancedFiltersMenuSection
                id='country'
                title='Country of Operation'
                advancedFilterKeys={['country']}
                clearSectionFilters={() =>
                    handleFormSelectChangeSingle(null, 'country')
                }
            >
                <DataGridAdvancedFilterFormSelect
                    label='SELECT COUNTRY'
                    controlId={`${dataTestPrefix}-country-select`}
                    dataTestId={`${dataTestPrefix}-country-select`}
                    options={countryOptions}
                    value={advancedFilters.country?.term}
                    onChange={value =>
                        handleFormSelectChangeSingle(
                            value,
                            'country',
                            'Country of Operation is'
                        )
                    }
                    clearable
                />
            </DataGridAdvancedFiltersMenuSection>
            <DataGridAdvancedFiltersMenuSection
                id='planned'
                title='Planned'
                advancedFilterKeys={['planned']}
                clearSectionFilters={() =>
                    handleRadioButtonGroupChange('', 'planned')
                }
            >
                <RadioGroup
                    name='planned'
                    label=''
                    data-test-id={`${dataTestPrefix}-planned-radio-group`}
                    value={advancedFilters.planned?.term}
                    options={plannedOptions}
                    onChange={value =>
                        handleRadioButtonGroupChange(
                            value,
                            'planned',
                            'Planned is'
                        )
                    }
                />
            </DataGridAdvancedFiltersMenuSection>
            <DataGridAdvancedFiltersMenuSection
                id='created-before-after'
                title='Created'
                advancedFilterKeys={['createdBefore', 'createdAfter']}
                clearSectionFilters={() => {
                    handleDateChange(null, 'createdBefore');
                    handleDateChange(null, 'createdAfter');
                }}
            >
                <DateBeforeAfter
                    prefixControlId={`${dataTestPrefix}-planned-end-date`}
                    beforeDateFilter={advancedFilters.createdBefore}
                    afterDateFilter={advancedFilters.createdAfter}
                    containerColumn
                    onBeforeDateChange={(date: moment.Moment) =>
                        handleDateChange(
                            date,
                            'createdBefore',
                            'Created before'
                        )
                    }
                    onAfterDateChange={(date: moment.Moment) =>
                        handleDateChange(date, 'createdAfter', 'Created after')
                    }
                    usePortal
                />
            </DataGridAdvancedFiltersMenuSection>
        </>
    );
};

export default AdminQuotesAdvancedFilterDataGrid;

function isEquipmentOnly(client: ClientAPI) {
    if (!client?.settings) {
        return false;
    }
    const lineOfBusiness = client.settings.find(
        s => s.settingTypeId === 'LINE_OF_BUSINESS'
    );
    return lineOfBusiness?.setting?.lineOfBusiness === 'EQUIPMENT';
}

function useClientOptions() {
    const { data: clients } = useClientsQuery();
    return useMemo(
        () =>
            clients
                ? clients.filter(isEquipmentOnly).map(item => ({
                      display: item.name,
                      id: item.id,
                  }))
                : [],
        [clients]
    );
}

function useCountryOptions() {
    const { data: countries } = useCountriesQuery();
    return useMemo(
        () =>
            countries
                ? countries.map(item => ({
                      display: item.description,
                      id: item.id,
                  }))
                : [],
        [countries]
    );
}

function useStatusOptions() {
    const { data: quoteStatuses } = useQuoteStatusesQuery();
    return useMemo(
        () =>
            quoteStatuses
                ? quoteStatuses
                      .map(item => ({
                          display: item.display,
                          id: item.id,
                      }))
                      .filter(item => ALLOWED_STATUSES.includes(item.id))
                : [],
        [quoteStatuses]
    );
}

function usePlannedOptions() {
    return useMemo<DisplayType[]>(
        () => [
            { id: 'Planned', display: 'Planned' },
            { id: 'Unplanned', display: 'Unplanned' },
        ],
        []
    );
}
